/** Document Property der SWOT-Positiv-Liste (Besichtigung-Tab). */
export const DOC_PROPERTY_SWOT_POSITIVES = "inspection_swot_positives";
/** Document Property der SWOT-Negativ-Liste (Besichtigung-Tab). */
export const DOC_PROPERTY_SWOT_NEGATIVES = "inspection_swot_negatives";

/** Ein einzelner SWOT-Eintrag, wie er in der Document-Property `inspection_swot_*` liegt. */
export interface SwotItem {
    description: string;

    [key: string]: unknown;
}

/** Modell einer SWOT-Liste (Document Property `inspection_swot_positives`/`_negatives`). */
export interface SwotModel {
    items: SwotItem[];
}

/**
 * SWOT-Standard-Positivsätze zur energetischen Einstufung (#18102). Schlüssel ist die Effizienzklasse
 * aus dem AVM-Energiewert-Output (`energy_efficiency`). D und E erhalten laut Ticket keine gesonderte
 * Würdigung, F–H stehen in {@link ENERGY_EFFICIENCY_SWOT_NEGATIVES}.
 */
export const ENERGY_EFFICIENCY_SWOT_POSITIVES: Readonly<Record<string, string>> = {
    "A+": "sehr gute energetische Gebäudeeigenschaften",
    A: "gute energetische Gebäudeeigenschaften",
    B: "gute energetische Gebäudeeigenschaften",
    C: "gute energetische Gebäudeeigenschaften",
};

/**
 * SWOT-Standard-Negativsätze zur energetischen Einstufung (#18102), analog zu
 * {@link ENERGY_EFFICIENCY_SWOT_POSITIVES}.
 */
export const ENERGY_EFFICIENCY_SWOT_NEGATIVES: Readonly<Record<string, string>> = {
    F: "mäßige energetische Gebäudeeigenschaften",
    G: "mäßige energetische Gebäudeeigenschaften",
    H: "schlechte energetische Gebäudeeigenschaft",
};

function lookupSentence(sentences: Readonly<Record<string, string>>, energyEfficiency: unknown): string | null {
    if (typeof energyEfficiency !== "string") return null;
    return sentences[energyEfficiency.trim().toUpperCase()] ?? null;
}

function withSentence(items: SwotItem[] | null | undefined, sentence: string | null): SwotItem[] {
    const existing = items ?? [];
    if (!sentence) return existing;
    if (existing.some((item) => item?.description?.trim() === sentence)) return existing;
    return [...existing, {description: sentence}];
}

/**
 * Standard-SWOT-Positivsatz zu einer energetischen Effizienzklasse.
 * @param energyEfficiency Effizienzklasse aus dem Energiewert-Output (z.B. "A+", "C")
 * @return der Standardsatz oder null, wenn die Klasse keinen Positiv-Satz vorsieht
 */
export function energyEfficiencySwotPositive(energyEfficiency: unknown): string | null {
    return lookupSentence(ENERGY_EFFICIENCY_SWOT_POSITIVES, energyEfficiency);
}

/**
 * Standard-SWOT-Negativsatz zu einer energetischen Effizienzklasse.
 * @param energyEfficiency Effizienzklasse aus dem Energiewert-Output (z.B. "F", "H")
 * @return der Standardsatz oder null, wenn die Klasse keinen Negativ-Satz vorsieht
 */
export function energyEfficiencySwotNegative(energyEfficiency: unknown): string | null {
    return lookupSentence(ENERGY_EFFICIENCY_SWOT_NEGATIVES, energyEfficiency);
}

/**
 * Ergänzt eine SWOT-Positiv-Liste um den Standardsatz zur energetischen Einstufung (#18102).
 *
 * Bewusst nur für Listen aufzurufen, die ausschließlich aus dem BesiConverter stammen (also noch nicht
 * als Document Property gespeichert sind) — sobald der Nutzer die Liste bearbeitet hat, ist sie
 * maßgeblich und wird nicht mehr automatisch ergänzt. Voraussetzung ist außerdem, dass überhaupt
 * Energieausweis-Daten vorliegen (siehe {@link hasEnergyCertificateData}).
 *
 * @param items SWOT-Positiv-Einträge aus dem BesiConverter
 * @param energyEfficiency Effizienzklasse aus dem Energiewert-Output (`energy_efficiency`)
 * @return die (ggf. ergänzte) Liste; unverändert, wenn kein Satz greift oder er bereits enthalten ist
 */
export function withEnergyEfficiencySwotPositive(
    items: SwotItem[] | null | undefined,
    energyEfficiency: unknown,
): SwotItem[] {
    return withSentence(items, energyEfficiencySwotPositive(energyEfficiency));
}

/**
 * Ergänzt eine SWOT-Negativ-Liste um den Standardsatz zur energetischen Einstufung (#18102), analog zu
 * {@link withEnergyEfficiencySwotPositive}.
 *
 * @param items SWOT-Negativ-Einträge aus dem BesiConverter
 * @param energyEfficiency Effizienzklasse aus dem Energiewert-Output (`energy_efficiency`)
 * @return die (ggf. ergänzte) Liste; unverändert, wenn kein Satz greift oder er bereits enthalten ist
 */
export function withEnergyEfficiencySwotNegative(
    items: SwotItem[] | null | undefined,
    energyEfficiency: unknown,
): SwotItem[] {
    return withSentence(items, energyEfficiencySwotNegative(energyEfficiency));
}

/** SWOT-Standardsatz für den Eingabeparameter `energy_photovoltaic_present` (#18102), deckungsgleich mit
 * dem Satz, den der BesiConverter für dasselbe Merkmal erzeugt (`SwotPvAnlageVorhanden`). */
export const PHOTOVOLTAIC_SWOT_SENTENCE = "Photovoltaik-Anlage vorhanden";

/** SWOT-Standardsatz für den Eingabeparameter `energy_solar_thermal_present` (#18102), analog zu
 * {@link PHOTOVOLTAIC_SWOT_SENTENCE}. */
export const SOLAR_THERMAL_SWOT_SENTENCE = "Solarthermie-Anlage vorhanden";

/**
 * Ergänzt eine SWOT-Positiv-Liste um den Standardsatz "Photovoltaik-Anlage vorhanden" (#18102), sobald
 * der Eingabeparameter `energy_photovoltaic_present` gesetzt ist. Gilt dieselbe Einschränkung auf
 * BesiConverter-Listen wie bei {@link withEnergyEfficiencySwotPositive}.
 *
 * @param items SWOT-Positiv-Einträge aus dem BesiConverter
 * @param photovoltaicPresent Wert des Eingabeparameters `energy_photovoltaic_present`
 */
export function withPhotovoltaicSwotPositive(
    items: SwotItem[] | null | undefined,
    photovoltaicPresent: unknown,
): SwotItem[] {
    return withSentence(items, photovoltaicPresent === true ? PHOTOVOLTAIC_SWOT_SENTENCE : null);
}

/**
 * Ergänzt eine SWOT-Positiv-Liste um den Standardsatz "Solarthermie-Anlage vorhanden" (#18102), analog zu
 * {@link withPhotovoltaicSwotPositive}.
 *
 * @param items SWOT-Positiv-Einträge aus dem BesiConverter
 * @param solarThermalPresent Wert des Eingabeparameters `energy_solar_thermal_present`
 */
export function withSolarThermalSwotPositive(
    items: SwotItem[] | null | undefined,
    solarThermalPresent: unknown,
): SwotItem[] {
    return withSentence(items, solarThermalPresent === true ? SOLAR_THERMAL_SWOT_SENTENCE : null);
}

/**
 * Die Energiekennwerte aus dem Energieausweis. Nur diese Parameter belegen, dass tatsächlich ein
 * Energieausweis ausgewertet wurde — allgemeine Gebäudeangaben des Energieausweis-Tabs
 * (`year_of_construction` & Co.) sind praktisch immer gefüllt und taugen nicht als Beleg.
 */
export const ENERGY_CERTIFICATE_VALUE_PARAMS: readonly string[] = [
    "energy_final_consumption",
    "energy_final_demand",
    "energy_primary_consumption",
    "energy_primary_demand",
];

/**
 * True, wenn zur Bewertung Energieausweis-Kennwerte vorliegen — geprüft werden ausschließlich die
 * Parameter aus {@link ENERGY_CERTIFICATE_VALUE_PARAMS}, dafür über alle drei Schichten, in denen sie
 * gepflegt werden können: Energieausweis-Tab (`ENERGY_CERT`), dessen IDP-Auslesung
 * (`IDP_ENERGY_CERTIFICATE`) und die Overview-Übersteuerung (`AVM`). Die Kennwerte stehen laut Spec
 * sowohl auf der Seite `object/energy` als auch auf `object/overview`, und die Overview-Eingabe gewinnt
 * in der Parameter-Auflösung — sie fließt also genauso in `energy_efficiency` ein.
 *
 * Gate für die energetischen SWOT-Standardsätze: ohne solche Kennwerte ist `energy_efficiency` nur die
 * AVM-Schätzung und rechtfertigt keinen Standardsatz im Gutachten (#18102).
 *
 * @param energyCertificateParams Parameter der Energieausweis-Schicht
 * @param idpEnergyCertificateParams Parameter der Energieausweis-IDP-Schicht
 * @param avmParams Parameter der AVM-/Overview-Schicht
 */
export function hasEnergyCertificateData(
    energyCertificateParams: Record<string, unknown> | null | undefined,
    idpEnergyCertificateParams: Record<string, unknown> | null | undefined,
    avmParams: Record<string, unknown> | null | undefined,
): boolean {
    const hasValue = (params: Record<string, unknown> | null | undefined) =>
        ENERGY_CERTIFICATE_VALUE_PARAMS.some((parameter) => {
            const value = params?.[parameter];
            return value !== null && value !== undefined && value !== "";
        });
    return hasValue(energyCertificateParams) || hasValue(idpEnergyCertificateParams) || hasValue(avmParams);
}

/**
 * True, wenn der Nutzer die SWOT-Analyse gepflegt hat — also *eine* der beiden Listen als Document
 * Property gespeichert ist.
 *
 * Bewusst über beide Listen hinweg: eine manuelle Eingabe soll verhindern, dass eine spätere Änderung
 * am Energieausweis noch Standardsätze nachträgt — auch in der jeweils anderen Liste (#18102). Sonst
 * tauchte in einer von Hand gepflegten SWOT-Analyse später ein automatischer Satz auf, nur weil bisher
 * ausschließlich die andere Liste bearbeitet wurde.
 *
 * @param persistedDocumentProperties die gespeicherten Document Properties der Bewertung
 */
export function hasUserEditedSwot(persistedDocumentProperties: Record<string, unknown> | null | undefined): boolean {
    return [DOC_PROPERTY_SWOT_POSITIVES, DOC_PROPERTY_SWOT_NEGATIVES].some(
        (property) => persistedDocumentProperties?.[property] != null,
    );
}
